import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Clock, Sparkles } from "lucide-react";
import { QuizApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { toast } from "sonner";

type PublicQuizQuestion = {
  id: string;
  text: string;
  // for demo we keep options minimal; real options/answers can be wired to API later
  options: string[];
  correctIndex: number;
};

export function PublicQuizContent() {
  const [searchParams] = useSearchParams();
  const quizIdParam = searchParams.get("quizId");
  const quizId =
    quizIdParam != null && quizIdParam !== "" && !Number.isNaN(Number(quizIdParam))
      ? Number(quizIdParam)
      : null;

  // Demo quiz data; in real flow, fetch by quizId from route params
  const questions: PublicQuizQuestion[] = useMemo(
    () => [
      {
        id: "q1",
        text: "Which of the following benefits is exclusive to Gold Tier members?",
        options: ["10% off", "Free Shipping", "Free Returns", "Priority Support"],
        correctIndex: 1,
      },
      {
        id: "q2",
        text: "How long is the free shipping offer valid?",
        options: ["1 month", "3 months", "6 months", "12 months"],
        correctIndex: 2,
      },
      {
        id: "q3",
        text: "Which code applies the summer discount?",
        options: ["HOT10", "HEAT15", "SUMMER20", "SUN25"],
        correctIndex: 2,
      },
    ],
    []
  );

  // Steps: verify -> questions -> result
  const [step, setStep] = useState<"verify" | "questions" | "result">("verify");

  // Verification state
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Quiz state
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<number[]>(Array(questions.length).fill(-1));

  const sendOtp = () => {
    if (!/^\d{7,15}$/.test(mobile)) return;
    setOtpSent(true);
  };

  const verifyOtp = async () => {
    if (!otp || otp.trim().length < 4) return;
    setIsVerifying(true);
    try {
      const response = await QuizApi.getQuizByQuizId({
        p_QuizId: quizId ?? 0,
        p_MobileNo: Number(mobile) || 0,
      });

      if (
        response?.error_msg &&
        typeof response.error_msg === "string" &&
        response.error_msg.trim()
      ) {
        toast.error(response.error_msg);
        return;
      }

      // Response integration will be wired later
      console.log("GetQuizByQuizId response:", response);
      setStep("questions");
    } catch (error) {
      console.error("Error fetching quiz by quiz id:", error);
      toast.error(HandleError(error) || "Failed to load quiz. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const selectOption = (index: number) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIdx] = index;
      return next;
    });
  };

  const goNext = () => {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx((i) => i + 1);
    } else {
      setStep("result");
    }
  };

  const goPrev = () => {
    if (currentIdx > 0) setCurrentIdx((i) => i - 1);
  };

  const score = useMemo(() => {
    return answers.reduce((acc, ans, i) => (ans === questions[i].correctIndex ? acc + 1 : acc), 0);
  }, [answers, questions]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-amber-100 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="rounded-2xl border border-indigo-100/70 bg-white/80 backdrop-blur-sm p-6 shadow-sm text-center space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 uppercase tracking-wide">
            <Sparkles className="h-3.5 w-3.5" />
            Gamification Quiz
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-700 via-violet-700 to-amber-600 bg-clip-text text-transparent">
            Play, Answer, and Win Rewards
          </h1>
          {quizId != null ? (
            <p className="text-sm text-muted-foreground">Quiz ID: {quizId}</p>
          ) : null}
          <p className="text-muted-foreground">
            Verify your number and complete the quiz to collect rewards.
          </p>
        </div>

        <div className="space-y-6">
      {step === "verify" && (
        <Card className="shadow-sm border-indigo-100/70 bg-white/95">
          <CardHeader>
            <CardTitle>Verify Mobile Number</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number</Label>
              <Input
                id="mobile"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d{0,15}$/.test(v)) setMobile(v);
                }}
              />
            </div>
            {otpSent && (
              <div className="space-y-2">
                <Label htmlFor="otp">OTP</Label>
                <Input
                  id="otp"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </div>
            )}
            <div className="flex gap-2">
              {!otpSent ? (
                <Button onClick={sendOtp} disabled={!/^\d{7,15}$/.test(mobile)}>
                  Send OTP
                </Button>
              ) : (
                <Button
                  onClick={verifyOtp}
                  disabled={isVerifying || !otp || otp.trim().length < 4}
                >
                  {isVerifying ? "Loading..." : "Verify & Start Quiz"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {step === "questions" && (
        <Card className="shadow-sm border-indigo-100/70 bg-white/95">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Question {currentIdx + 1} of {questions.length}</CardTitle>
              <div className="flex items-center text-sm text-muted-foreground">
                <Clock className="h-4 w-4 mr-1" /> {questions.length * 2} min est.
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-base font-medium">{questions[currentIdx].text}</div>
            <div className="space-y-2">
              {questions[currentIdx].options.map((opt, idx) => {
                const selected = answers[currentIdx] === idx;
                return (
                  <Button
                    key={idx}
                    type="button"
                    variant={selected ? "default" : "outline"}
                    className={`w-full justify-start ${selected ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
                    onClick={() => selectOption(idx)}
                  >
                    {opt}
                  </Button>
                );
              })}
            </div>
            <div className="flex justify-between pt-2">
              <Button type="button" variant="outline" onClick={goPrev} disabled={currentIdx === 0}>
                Previous
              </Button>
              <Button type="button" onClick={goNext} disabled={answers[currentIdx] === -1}>
                {currentIdx === questions.length - 1 ? "Finish" : "Next"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "result" && (
        <Card className="shadow-sm border-indigo-100/70 bg-white/95">
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              <div className="font-semibold">You scored {score} out of {questions.length}</div>
            </div>
            <div className="text-sm text-muted-foreground">
              Thanks for participating! Collect your reward to finalize this quiz.
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white w-full">
              Collect Reward
            </Button>
          </CardContent>
        </Card>
      )}
        </div>
      </div>
    </div>
  );
}

